import * as React from 'react';
import {Helmet} from 'react-helmet';

import {Orbs} from 'shells/ai-homepage/orbs/orbs';
import {AiHomepageHeader} from 'shells/ai-homepage/text/ai-homepage-header';
import {headTitle} from 'shells/document/head-title';
import {GridList} from 'shells/grid-list/grid-list';
import {Stack} from 'shells/layout/stack/stack';
import {LEAD_REPORT_TOUR_CREATE_REPORT_ID} from 'shells/tours/tours';

import {DefaultReportsSection} from './default-reports/default-reports-section';
import {useGetRecentlySavedReports} from './graphql/hooks/use-get-recently-saved-reports';
import {useGetRecentlyViewedReports} from './graphql/hooks/use-get-recently-viewed-reports';
import {PromptSection} from './prompt-section';
import {ReportCardStack} from './report-card-stack';
import {ReportGeneratingState} from './report-generating-state';

import styles from './overview-page.module.css';

export function OverviewPage(): React.ReactElement {
    const [isGenerating, setIsGenerating] = React.useState(false);

    const {recentlySavedReports, isLoading: isLoadingRecentReports} = useGetRecentlySavedReports();

    const {recentlyViewedReports, isLoading: isLoadingRecentlyViewedReports} =
        useGetRecentlyViewedReports();

    const shouldShowRecentlySavedReportsEmptyState = Boolean(
        !isLoadingRecentReports &&
            !recentlySavedReports.length &&
            !isLoadingRecentlyViewedReports &&
            recentlyViewedReports.length
    );

    const shouldShowRecentlyViewedReportsEmptyState = Boolean(
        !isLoadingRecentlyViewedReports &&
            !recentlyViewedReports.length &&
            !isLoadingRecentReports &&
            recentlySavedReports.length
    );

    return (
        <>
            <Helmet>
                <title>{headTitle('Reports overview')}</title>
            </Helmet>
            {isGenerating ? (
                <ReportGeneratingState onDiscard={() => setIsGenerating(false)} />
            ) : (
                <div className='flex'>
                    <div className='overflow-hidden full-width'>
                        <Orbs
                            config={[
                                {top: -5, left: 0},
                                {top: -10, left: 30},
                                {top: 0, left: 60},
                                {top: -5, left: 90},
                            ]}
                        >
                            <div className={styles['content-container']}>
                                <Stack spacing={48} isFullWidth={true} alignItems='center'>
                                    <div
                                        id={LEAD_REPORT_TOUR_CREATE_REPORT_ID}
                                        className='full-width flex flex-dir-col gap-16 align-center'
                                    >
                                        <AiHomepageHeader
                                            text='What report can we create for you?'
                                            color='dark'
                                        />
                                        <PromptSection onGenerate={() => setIsGenerating(true)} />
                                    </div>
                                    <div className='full-width'>
                                        <GridList numColumns={2} gridGap={16}>
                                            <ReportCardStack
                                                reports={recentlyViewedReports}
                                                variant='viewed-reports'
                                                isLoading={isLoadingRecentlyViewedReports}
                                                shouldShowEmptyState={
                                                    shouldShowRecentlyViewedReportsEmptyState
                                                }
                                            />
                                            <ReportCardStack
                                                reports={recentlySavedReports}
                                                variant='saved-reports'
                                                isLoading={isLoadingRecentReports}
                                                shouldShowEmptyState={
                                                    shouldShowRecentlySavedReportsEmptyState
                                                }
                                            />
                                        </GridList>
                                    </div>
                                </Stack>
                                <DefaultReportsSection />
                            </div>
                        </Orbs>
                    </div>
                </div>
            )}
        </>
    );
}
